import React, { useEffect, useState } from "react";
import { db } from "../../firebase";
import { doc, updateDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, MessageCircle, ThumbsUp, Flag, X, Trash2 } from "lucide-react";
import "./NotificationSystem.css";
import AppealModal from "../Reports/AppealModal";
import useNotifications from "./useNotifications";

const NotificationSystem = () => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [currentNotification, setCurrentNotification] = useState(null);
  const [showAppealModal, setShowAppealModal] = useState(false);
  const [appealNotification, setAppealNotification] = useState(null);
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  
  const { 
    notifications, 
    unreadCount, 
    markAllAsRead, 
    deleteNotification,
    clearAllNotifications,
    newNotificationReceived 
  } = useNotifications(30);
  
  const navigate = useNavigate();

  useEffect(() => {
    // Check if dark theme is applied
    const isDark = document.body.classList.contains('dark-theme');
    setIsDarkTheme(isDark);
    
    // Listen for theme changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          setIsDarkTheme(document.body.classList.contains('dark-theme'));
        }
      });
    });
    
    observer.observe(document.body, { attributes: true });
    
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Show the most recent notification if it's new and unread
    if (newNotificationReceived && notifications.length > 0) {
      const latestNotification = notifications.find(n => !n.read);
      if (latestNotification) {
        setCurrentNotification(latestNotification);
        setShowNotification(true);
        
        // Auto-hide after 5 seconds
        setTimeout(() => {
          setShowNotification(false);
        }, 5000);
      }
    }
  }, [newNotificationReceived, notifications]);

  const handleClose = () => {
    setShowNotification(false);
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read
    if (!notification.read) {
      const notificationRef = doc(db, "notifications", notification.id);
      await updateDoc(notificationRef, { read: true });
    }
    
    // Handle content removal notification with appeal option
    if (notification.type === 'moderation' && notification.canAppeal) {
      // First check if the content is still removed
      await checkContentStatus(notification);
      return;
    }
    
    // Navigate to the linked page
    if (notification.link) {
      setShowDropdown(false);
      navigate(notification.link);
    }
  };
  
  // Check if content is still removed before showing appeal option
  const checkContentStatus = async (notification) => {
    try {
      const { contentType, queryId, contentId } = notification;
      let isStillRemoved = true;
      
      // Need to implement a check to the actual content to see if it's still removed
      // For now, we'll just assume it is and show the appeal option
      
      if (isStillRemoved) {
        setAppealNotification(notification);
        setShowAppealModal(true);
        setShowDropdown(false);
      } else {
        // If it's already restored, just navigate to the content
        navigate(notification.link);
      }
    } catch (error) {
      console.error("Error checking content status:", error);
      // If there's an error, still allow appeal as a fallback
      setAppealNotification(notification);
      setShowAppealModal(true);
    }
  };

  const handleBellClick = () => {
    setShowDropdown(!showDropdown);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'comment':
        return <MessageCircle size={18} />;
      case 'vote':
        return <ThumbsUp size={18} />;
      case 'moderation':
        return <Flag size={18} />;
      case 'appeal':
        return <Flag size={18} />;
      default:
        return <Bell size={18} />;
    }
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  return (
    <div className={`notification-system ${isDarkTheme ? 'dark-theme' : ''}`}>
      <div className="notification-bell-container">
        <div className="notification-bell" onClick={handleBellClick}>
          <Bell size={20} />
          {unreadCount > 0 && (
            <span className={`notification-badge ${newNotificationReceived ? 'new-notification' : ''}`}>
              {unreadCount}
            </span>
          )}
        </div>
        
        {showDropdown && (
          <div className="notifications-dropdown">
            <div className="notifications-header">
              <div className="notifications-header-title">
                <h3>Notifications</h3>
                {notifications.length > 0 && (
                  <span className="notifications-count-pill">{notifications.length}</span>
                )}
              </div>
              {notifications.length > 0 && (
                <div className="notifications-header-actions">
                  {unreadCount > 0 && (
                    <button className="mark-all-read-btn" onClick={markAllAsRead} title="Mark all as read">
                      <CheckCheck size={13} />
                      <span>Mark Read</span>
                    </button>
                  )}
                  <button className="clear-all-btn" onClick={clearAllNotifications} title="Clear all notifications">
                    <Trash2 size={13} />
                    <span>Clear All</span>
                  </button>
                </div>
              )}
            </div>
            <div className="notifications-content">
              {notifications.length === 0 ? (
                <div className="no-notifications">
                  <div className="no-notifications-icon">🔔</div>
                  <p>All clear! No notifications pending.</p>
                </div>
              ) : (
                notifications.map(notification => (
                  <div 
                    key={notification.id} 
                    className={`notification-item ${notification.read ? '' : 'unread'}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="notification-icon">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="notification-content">
                      <p className="notification-message">{notification.message}</p>
                      <span className="notification-time">
                        {formatTimeAgo(notification.createdAt)}
                      </span>
                      {notification.type === 'moderation' && notification.canAppeal && (
                        <button 
                          className="appeal-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAppealNotification(notification);
                            setShowAppealModal(true);
                            setShowDropdown(false);
                          }}
                        >
                          Appeal this decision
                        </button>
                      )}
                    </div>
                    <button 
                      className="notification-dismiss-btn"
                      title="Dismiss notification"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNotification(notification.id);
                      }}
                    >
                      <X size={13} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
      
      {showNotification && currentNotification && (
        <div className="notification-toast" onClick={() => handleNotificationClick(currentNotification)}>
          <div className="notification-toast-content">
            <div className="notification-icon">
              {getNotificationIcon(currentNotification.type)}
              <div className="notification-dot"></div>
            </div>
            <p className="notification-message">
              {currentNotification.message}
            </p>
            <button className="notification-close" onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}>
              <X size={16} />
            </button>
          </div>
        </div>
      )}
      
      {/* Appeal Modal */}
      {showAppealModal && appealNotification && (
        <AppealModal
          contentId={appealNotification.contentId || ''}
          contentType={appealNotification.contentType || 'content'}
          removedText={'Content was removed per community guidelines'}
          queryId={appealNotification.queryId || null}
          onClose={() => {
            setShowAppealModal(false);
            setAppealNotification(null);
          }}
        />
      )}
    </div>
  );
};

export default NotificationSystem; 